'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'

/* ─────────────────────────────────────────────────────────────
   ONE AdSlot for every placement (single source of truth).

   THREE modes, flipped by ONE switch (AD_MODE, via the NEXT_PUBLIC_ADS_MODE
   env var — NEXT_PUBLIC_ vars are inlined at BUILD time, so changing it
   REQUIRES a redeploy, and it must be set in BOTH Vercel env sets):
     • 'house' (DEFAULT) — no third-party provider yet, so render the
        first-party HOUSE AD ("Advertise with Masjids.in"). HONEST: it's
        a real first-party ad that also markets the inventory to potential
        advertisers. Never a fake third-party ad/product.
     • 'live'  — serve real booked ads (/api/ads/serve); the house ad is
        the fallback when nothing is booked. Flip here when a provider is wired.
     • 'off'   — render nothing (all slots hidden).

   FLUID sizes (Faheem: "the ads should not spoil the pages by blank
   spaces"): every variant is width:100% with a min-height — the creative
   IS its container, so a fixed box can never sit centered in a wider
   band leaving dead space. Shapes: leaderboard (wide strip) · rectangle
   (stacked card) · mobileBanner (one-line row) · card (height:100%,
   stretches to match neighbouring grid cards). The 160×600 skyscraper
   was REMOVED (Faheem's call) — rails get the rectangle. Legacy variant
   names (banner/native/sidebar) alias onto these — call sites unchanged.

   Excluded surfaces (never mount this): shaheed archive/detail/contribute,
   adopt directory/detail, claim wizard, onboard, prayer surfaces,
   donation/checkout, waqf legal pages, janaza notices, auth flows.
───────────────────────────────────────────────────────────── */

export type AdMode = 'house' | 'live' | 'off'
const RAW_MODE = String(process.env.NEXT_PUBLIC_ADS_MODE || '').toLowerCase()
export const AD_MODE: AdMode = RAW_MODE === 'live' ? 'live' : RAW_MODE === 'off' ? 'off' : 'house'

type AdShape = 'leaderboard' | 'rectangle' | 'mobileBanner' | 'card'
export type AdVariant = AdShape | 'banner' | 'native' | 'sidebar'

// Legacy variant names → shape (so existing placements keep working).
// 'sidebar' now maps to the rectangle — the skyscraper is gone.
const TO_SHAPE: Record<AdVariant, AdShape> = {
  leaderboard: 'leaderboard', rectangle: 'rectangle', mobileBanner: 'mobileBanner', card: 'card',
  banner: 'leaderboard', native: 'rectangle', sidebar: 'rectangle',
}

// Live-mode space reservation, proportionate to the fluid min-heights —
// never reserve a 600px column for nothing.
const VARIANT_MIN_H: Record<AdShape, number> = {
  leaderboard: 90, rectangle: 220, mobileBanner: 50, card: 220,
}

export default function AdSlot(props: {
  placement: string
  variant?: AdVariant
  city?: string | null
  locality?: string | null
  className?: string
}) {
  const { placement, variant = 'native', className = '' } = props
  const size = TO_SHAPE[variant] || 'rectangle'
  const { isFlagEnabled } = useFeatureFlags()

  // sponsorTools = master advertising switch (admin-toggleable in /admin/features). OFF hides
  // EVERY public ad slot — the single source of truth for whether ads render at all.
  if (!isFlagEnabled('sponsorTools' as any)) return null
  if (AD_MODE === 'off') return null
  if (AD_MODE === 'house') return <HouseAd size={size} placement={placement} className={className} />
  // AD_MODE === 'live'
  return <AdSlotLive {...props} variant={variant} />
}

/* ── First-party HOUSE AD creative — a clean, professional banner: SOLID brand
   green, a small crisp white Masjids.in logo, headline + one subline, and a real
   gold CTA button. FLUID: every shape fills its container's width (card also its
   height) — no fixed px box, no dead space. Copy is HONEST: no reach numbers we
   can't evidence (the old "Reach 1,200+ masjid communities" was a stale hardcoded
   claim). Links to /advertise. The "Ad" label is present at EVERY shape and size. */
function HouseAd({ size, placement, className = '' }: { size: AdShape; placement: string; className?: string }) {
  return (
    <Link
      href="/advertise"
      className={className}
      data-placement={placement}
      aria-label="Advertise on Masjids.in"
      style={{ display: 'block', width: '100%', height: size === 'card' ? '100%' : undefined, textDecoration: 'none' }}
    >
      {/* styled-jsx only scopes NATIVE elements, not the <Link> component — so the
          styled ROOT is this inner <span>, which guarantees the rules apply. */}
      <span className={`adhouse adhouse-${size}`}>
        <span className="adhouse-tag">Ad</span>
        {/* small, crisp white wordmark — never a background watermark */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className="adhouse-logo" src="/homepage-assets/brand-logo-footer.png" alt="Masjids.in" loading="lazy" decoding="async" />
        <span className="adhouse-text">
          <span className="adhouse-title">Advertise on Masjids.in</span>
          <span className="adhouse-sub">Reach the community around the masjid — respectful, clearly-labeled placements.</span>
        </span>
        <span className="adhouse-cta">See placements →</span>
      </span>
      <style jsx>{`
        /* FLUID: no fixed px width/height anywhere — width:100% + min-height per shape.
           The creative fills whatever container mounts it, so it can never sit as a
           fixed box centered in a wider band (that WAS the dead-space bug). */
        .adhouse { position:relative; display:flex; box-sizing:border-box; width:100%; overflow:hidden; text-decoration:none; border-radius:12px; background:#0E7C61; border:1px solid rgba(255,255,255,.14); box-shadow:0 2px 10px rgba(11,19,43,.10); transition:background .15s; }
        .adhouse:hover { background:#0C6B53; }
        /* The "Ad" label is NEVER hidden — clearly-labeled at every shape and size. */
        .adhouse-tag { position:absolute; top:5px; right:8px; font-size:8.5px; font-weight:800; letter-spacing:.12em; text-transform:uppercase; color:rgba(255,255,255,.5); }
        .adhouse-logo { display:block; width:auto; height:26px; object-fit:contain; flex:none; }
        .adhouse-text { display:flex; flex-direction:column; min-width:0; }
        .adhouse-title { color:#fff; font-weight:800; line-height:1.15; letter-spacing:-.01em; }
        .adhouse-sub { color:rgba(255,255,255,.85); line-height:1.35; }
        .adhouse-cta { display:inline-flex; align-items:center; justify-content:center; background:#D4AF37; color:#0B132B; font-weight:800; border-radius:999px; white-space:nowrap; flex:none; }

        /* leaderboard — wide strip: [logo] [text] ........ [CTA] */
        .adhouse-leaderboard { min-height:90px; flex-direction:row; align-items:center; gap:16px; padding:14px 20px; }
        .adhouse-leaderboard .adhouse-logo { height:30px; }
        .adhouse-leaderboard .adhouse-text { flex:1; gap:3px; }
        .adhouse-leaderboard .adhouse-title { font-size:18px; }
        .adhouse-leaderboard .adhouse-sub { font-size:12.5px; }
        .adhouse-leaderboard .adhouse-cta { font-size:13.5px; padding:10px 18px; }

        /* rectangle — stacked card, centered (also the rail shape — no more skyscraper) */
        .adhouse-rectangle { min-height:220px; flex-direction:column; align-items:center; justify-content:center; gap:14px; padding:26px 22px; text-align:center; }
        .adhouse-rectangle .adhouse-logo { height:30px; }
        .adhouse-rectangle .adhouse-text { align-items:center; gap:8px; }
        .adhouse-rectangle .adhouse-title { font-size:19px; line-height:1.22; }
        .adhouse-rectangle .adhouse-sub { font-size:13px; }
        .adhouse-rectangle .adhouse-cta { font-size:14px; padding:11px 22px; }

        /* mobile banner — tight single row */
        .adhouse-mobileBanner { min-height:50px; flex-direction:row; align-items:center; gap:10px; padding:8px 12px; }
        .adhouse-mobileBanner .adhouse-logo { height:18px; }
        .adhouse-mobileBanner .adhouse-text { flex:1; }
        .adhouse-mobileBanner .adhouse-title { font-size:13px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .adhouse-mobileBanner .adhouse-sub { display:none; }
        .adhouse-mobileBanner .adhouse-cta { font-size:12px; padding:7px 13px; }
        .adhouse-mobileBanner .adhouse-tag { top:2px; right:6px; font-size:7.5px; }

        /* card — fills its GRID CELL (height:100%), so align-items:stretch equalizes
           it against the neighbouring directory cards. Stacked like the rectangle. */
        .adhouse-card { height:100%; min-height:220px; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:22px 18px; text-align:center; }
        .adhouse-card .adhouse-logo { height:28px; }
        .adhouse-card .adhouse-text { align-items:center; gap:8px; }
        .adhouse-card .adhouse-title { font-size:17px; line-height:1.24; }
        .adhouse-card .adhouse-sub { font-size:12.5px; }
        .adhouse-card .adhouse-cta { font-size:13px; padding:10px 18px; }

        /* Small screens: the leaderboard tightens to a row — the "Ad" tag stays. */
        @media (max-width:768px) {
          .adhouse-leaderboard { min-height:56px; gap:10px; padding:8px 12px; }
          .adhouse-leaderboard .adhouse-logo { height:18px; }
          .adhouse-leaderboard .adhouse-sub { display:none; }
          .adhouse-leaderboard .adhouse-title { font-size:13px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .adhouse-leaderboard .adhouse-cta { font-size:12px; padding:7px 13px; }
          .adhouse-leaderboard .adhouse-tag { top:2px; right:6px; font-size:7.5px; }
        }
      `}</style>
    </Link>
  )
}

// ── LIVE serving (only when AD_MODE === 'live'). Lazy fetch from /api/ads/serve;
//    falls back to the same first-party HouseAd when nothing is booked.
type ServedAd = { id: string; name: string; type: string; image_url: string | null; link_url: string | null; alt_text: string | null }

function AdSlotLive({ placement, variant = 'native', city, locality, className = '' }: {
  placement: string; variant?: AdVariant; city?: string | null; locality?: string | null; className?: string
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [inView, setInView] = useState(false)
  const [ad, setAd] = useState<ServedAd | null>(null)
  const [resolved, setResolved] = useState(false)
  const size = TO_SHAPE[variant] || 'rectangle'
  // Proportionate reservation only (90/220/50): pre-resolve layout shift is avoided
  // without ever reserving a tall empty column for nothing.
  const minHeight = VARIANT_MIN_H[size]

  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect() }
    }, { rootMargin: '400px' })
    io.observe(el)
    return () => io.disconnect()
  }, [inView])

  useEffect(() => {
    if (!inView) return
    let alive = true
    const params = new URLSearchParams({ placement })
    if (city) params.set('city', city)
    if (locality) params.set('locality', locality)
    fetch(`/api/ads/serve?${params}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) { setAd(d?.ad || null); setResolved(true) } })
      .catch(() => { if (alive) setResolved(true) })
    return () => { alive = false }
  }, [inView, placement, city, locality])

  return (
    <div ref={ref} className={className} data-placement={placement} style={{ minHeight, width: '100%', height: size === 'card' ? '100%' : undefined }}>
      {ad ? (
        <a href={`/api/ads/click?id=${ad.id}`} className={`ad-slot ad-slot-${size}`} rel="nofollow sponsored" style={{ minHeight }}>
          <span className="ad-slot-label">Sponsored</span>
          <span className="ad-slot-body">
            {ad.image_url && <img src={ad.image_url} alt={ad.alt_text || ad.name} loading="lazy" decoding="async" />}
            <span className="ad-slot-copy">
              <strong>{ad.name}</strong>
              {ad.alt_text && <span>{ad.alt_text}</span>}
              <em>Visit →</em>
            </span>
          </span>
        </a>
      ) : resolved ? (
        <HouseAd size={size} placement={placement} />
      ) : (
        <div aria-hidden style={{ minHeight, width: '100%' }} />
      )}
    </div>
  )
}
